import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertNumberToWords } from "./utility";

function mapSeller(s) {
    return {
        name: s?.companyName || s?.name || "",
        subtitle: s?.companySubtitle || s?.subtitle || "",
    };
}

function buildFooterLines(fromSeller) {
    const s = fromSeller || {};
    const address = s.companyAddress || s.address || null;
    const phones = [s.companyPhone1 || s.phone1, s.companyPhone2 || s.phone2].filter(Boolean).join(" - ");
    const email = s.companyEmail || s.email || null;
    const line1 = [address, phones || null, email].filter(Boolean).join(" - ");

    const regs = [
        s.companyIce || s.ice ? `ICE: ${s.companyIce || s.ice}` : null,
        s.companyRc || s.rc ? `RC: ${s.companyRc || s.rc}` : null,
        s.companyPatente || s.patente ? `Patente: ${s.companyPatente || s.patente}` : null,
        s.companyIf || s.if ? `IF: ${s.companyIf || s.if}` : null,
        s.companyCnss || s.cnss ? `CNSS: ${s.companyCnss || s.cnss}` : null,
    ].filter(Boolean).join(" - ");

    return { line1, regs };
}

export const generateReceptionLikePdfMake = (
    note,
    {
        docType = "BON DE RECEPTION",
        showPrices = true,
        priceMode = "TTC",
        company = {},
    } = {}
) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const seller = mapSeller(company);
    const { line1: footerLine1, regs: footerLine2 } = buildFooterLines(company);

    const PAGE_W = doc.internal.pageSize.width;
    const PAGE_H = doc.internal.pageSize.height;
    const M = 20;
    const FOOTER_H = 22;

    const BRAND = {
        primary: [6, 145, 43],
        ink: [15, 23, 42],
        subtle: [100, 116, 139],
        line: [226, 232, 240],
        headerBg: [241, 245, 249],
    };

    const fmtMoney = (v) =>
        Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    const amountToWords = (n) =>
        convertNumberToWords(Math.round(n || 0)).toUpperCase() + " DH";

    const drawHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(...BRAND.primary);
        doc.text(seller.name || "MA SOCIETE", PAGE_W / 2, 22, { align: "center" });

        if (seller.subtitle) {
            doc.setFontSize(10);
            doc.text(seller.subtitle, PAGE_W / 2, 29, { align: "center" });
        }

        doc.setDrawColor(...BRAND.line);
        doc.line(M, 34, PAGE_W - M, 34);
    };

    const CARD_W = (PAGE_W - M * 3) / 2;
    const CARD_H = 45;
    const CARD_Y = 40;
    const CARD_MAX_CHARS = 62;

    const truncateLine = (value, maxChars = CARD_MAX_CHARS) => {
        if (value === null || value === undefined) return "";
        const text =
            typeof value === "string" ? value :
                typeof value === "number" ? String(value) :
                    JSON.stringify(value);

        return text.length > maxChars ? `${text.slice(0, maxChars - 3)}...` : text;
    };

    const drawCard = (x, title, lines) => {
        doc.setDrawColor(0);
        doc.roundedRect(x, CARD_Y, CARD_W, CARD_H, 3, 3);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...BRAND.ink);
        doc.text(String(title || ""), x + 5, CARD_Y + 8);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.subtle);

        (lines || []).forEach((line, i) => {
            const truncated = truncateLine(line);
            if (!truncated) return;
            doc.text(truncated, x + 5, CARD_Y + 16 + i * 6);
        });
    };

    const headers = showPrices
        ? ["#", "Article", "Qte", "UM", priceMode === "HT" ? "PU HT" : "PU TTC", priceMode === "HT" ? "Total HT" : "Total TTC"]
        : ["#", "Article", "Qte", "UM"];

    const rows = (note.lignes || []).map((l, i) =>
        showPrices
            ? [
                i + 1,
                l.articleNom,
                l.quantiteRecue,
                l.unite,
                fmtMoney(priceMode === "HT" ? l.prixUnitaireHT : l.prixUnitaireTTC),
                fmtMoney(priceMode === "HT" ? l.montantHT : l.montantTTC),
            ]
            : [i + 1, l.articleNom, l.quantiteRecue, l.unite]
    );

    const drawTotals = (y) => {
        const boxW = 70;
        const x = PAGE_W - M - boxW;

        doc.setFillColor(163, 240, 183);
        doc.roundedRect(x, y, boxW, 24, 3, 3, "F");

        doc.setFont("helvetica", "bold");
        doc.setTextColor(...BRAND.ink);

        doc.text("TOTAL TTC", x + 5, y + 10);
        doc.text(fmtMoney(note.montantTotalTTC), x + boxW - 5, y + 18, {
            align: "right",
        });
    };

    const drawFooter = (pageNo) => {
        doc.setDrawColor(0);
        doc.line(M, PAGE_H - FOOTER_H, PAGE_W - M, PAGE_H - FOOTER_H);

        doc.setFontSize(8);
        doc.setTextColor(80);

        if (footerLine1) {
            doc.text(footerLine1, PAGE_W / 2, PAGE_H - 12, { align: "center" });
        }
        if (footerLine2) {
            doc.text(footerLine2, PAGE_W / 2, PAGE_H - 7, { align: "center" });
        }

        doc.text(`Page ${pageNo}`, PAGE_W - M, PAGE_H - 6, { align: "right" });
    };

    const paymentLabel = (mode) => {
        switch (mode) {
            case "CARTE_BANCAIRE":
                return "Carte bancaire";
            case "ESPECES":
                return "Especes";
            case "CHEQUE":
                return "Cheque";
            case "VIREMENT":
                return "Virement";
            case "TRAITE":
                return "Traite";
            default:
                return mode || "-";
        }
    };

    drawHeader();

    const chequeSuffix =
        note.modeReglement?.toUpperCase() === "CHEQUE" && note.numeroCheque
            ? ` (N° cheque : ${note.numeroCheque})`
            : "";

    drawCard(M, docType, [
        `Numero : ${note.numero || "-"}`,
        `Date Reception : ${note.dateReception || "-"}`,
        `BC N° : ${note.commandeNumero || "N/A"}    /    BL N° : ${note.deliveryNoteNb || "N/A"}`,
        `Transporteur : ${note.transporteurName || "-"} (${note.transporteurMatricule || "-"})`,
        `Mode paiement : ${paymentLabel(note.modeReglement)}${chequeSuffix}`,
    ]);

    drawCard(
        M * 2 + CARD_W,
        "FOURNISSEUR",
        [
            note.fournisseurNom,
            note.fournisseurVille,
            `Tel : ${note.fournisseurTelephone || "-"}`,
        ]
    );

    const tableY = CARD_Y + CARD_H + 10;

    autoTable(doc, {
        startY: tableY,
        head: [headers],
        body: rows,
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: {
            fillColor: BRAND.headerBg,
            textColor: BRAND.ink,
            fontStyle: "bold",
        },
        didDrawPage: () => {
            drawFooter(doc.internal.getNumberOfPages());
        },
    });

    const yAfter = doc.lastAutoTable.finalY + 10;

    if (showPrices) {
        drawTotals(yAfter);
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.subtle);
        doc.text(
            `Arrete le present document a la somme de : ${amountToWords(note.montantTotalTTC)}`,
            M,
            yAfter + 30
        );
    }

    const pdfBlob = doc.output("bloburl");
    window.open(pdfBlob, "_blank");
};
